import { isObject } from 'lodash';
import * as Z from 'zod';
import _ from 'lodash';
import LocalStorageService from './localstorage-service.js';
import { DRAWER_TYPES } from '../../markups/components/leaderboard/drawer/drawer.js';

const PROBABILITY_STATE_KEY = 'probability_state';
const probabilityStateSchema = Z.object({
    type: Z.string().optional(),
    isExpanded: Z.boolean().optional(),
    round: Z.number().optional(),
    dataType: Z.string().optional(),
});

type State = Z.infer<typeof probabilityStateSchema>;
type Pages = typeof DRAWER_TYPES[keyof typeof DRAWER_TYPES];

export class ProbabilityStateService {
    private state: {
        [page in Pages]?: {
            [round: number]: State;
        }
    } = {};
    private storageService = LocalStorageService;

    constructor() {
        const storedState = this.storageService.getItem<State>(
            PROBABILITY_STATE_KEY,
        );

        if (storedState) {
            this.state = storedState;
        }
    }

    private saveState(page: Pages, dayNumber: string, state: unknown): void {
        const validationResult = probabilityStateSchema.safeParse(state);

        if (!validationResult.success) {
            console.warn('Invalid probability component state');
            return;
        }

        _.set(this.state, this.getPath(page, dayNumber), validationResult.data);
        this.storageService.setItem(PROBABILITY_STATE_KEY, this.state);
    }

    private getPath(page: Pages, dayNumber: string) {
        return `${page}.round_${dayNumber}`;
    }

    public getPageState(page: Pages) {
        return _.get(this.state, `${page}`);
    }

    public getDayState(page: Pages, dayNumber: string) {
        return _.get(this.state, this.getPath(page, dayNumber));
    }

    public flushState(page: Pages) {
        this.state[page] = {};
        this.storageService.setItem(PROBABILITY_STATE_KEY, this.state);
    }

    public setState(
        page: Pages,
        dayNumber: string | undefined,
        state: unknown,
    ): void {
        if (!isObject(state) || !dayNumber) {
            return;
        }

        const prevState =
            _.get(this.state, this.getPath(page, dayNumber)) || {};

        const mergedState = {
            ...prevState,
            ...state,
        };

        this.saveState(page, dayNumber, mergedState);
    }
}
